import React, { memo } from 'react';
import { View, Text, Image, StyleSheet } from 'react-native';
import type { CorkBoardThreadDetail } from '@/types/residents/corkBoard';
import { TagBadge } from './TagBadge';
import { CORK_BOARD_COLORS, corkBoardStyles } from './corkBoardStyles';
import { SEMANTIC_COLORS, PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { formatRelativeDate } from '@/utils/common/formatting';

interface ThreadHeaderProps {
  thread: CorkBoardThreadDetail;
}

export const ThreadHeader: React.FC<ThreadHeaderProps> = memo(({ thread }) => {
  const isAnnouncement = thread.isAnnouncement || thread.isPinned;

  return (
    <View style={styles.container}>
      {/* Announcement / Pin banner */}
      {isAnnouncement && (
        <View style={styles.announcementBanner}>
          <Text style={styles.announcementLabel}>
            {thread.isAnnouncement ? 'ANNOUNCEMENT' : 'PINNED'}
          </Text>
        </View>
      )}

      {/* Tags */}
      {thread.tags.length > 0 && (
        <View style={styles.tagsRow}>
          {thread.tags.map((tag) => (
            <TagBadge key={tag.id} tag={tag} size="medium" />
          ))}
        </View>
      )}

      {/* Title */}
      <Text style={styles.title}>{thread.title}</Text>

      {/* Author row */}
      <View style={styles.authorRow}>
        {thread.creator.photoUrl ? (
          <Image source={{ uri: thread.creator.photoUrl }} style={styles.avatarImage} />
        ) : (
          <View style={styles.avatar}>
            <Text style={corkBoardStyles.avatarText}>{thread.creator.initials}</Text>
          </View>
        )}
        <Text style={styles.authorName}>{thread.creator.name}</Text>
        <Text style={styles.dot}>·</Text>
        <Text style={styles.timestamp}>{formatRelativeDate(thread.createdAt)}</Text>
      </View>

      {/* Stats row */}
      <View style={styles.statsRow}>
        <Text style={styles.stat}>{thread.replyCount} {thread.replyCount === 1 ? 'reply' : 'replies'}</Text>
        <Text style={styles.stat}>{thread.viewCount} {thread.viewCount === 1 ? 'view' : 'views'}</Text>
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    gap: 10,
  },
  announcementBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  announcementLabel: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.8,
    color: CORK_BOARD_COLORS.announcementBorder,
  },
  tagsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: SEMANTIC_COLORS.text.primary,
    lineHeight: 28,
  },
  authorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: PRIMITIVE_COLORS.white10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarImage: {
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  authorName: {
    fontSize: 14,
    fontWeight: '500',
    color: SEMANTIC_COLORS.text.primary,
  },
  dot: {
    fontSize: 14,
    color: SEMANTIC_COLORS.text.muted,
  },
  timestamp: {
    fontSize: 13,
    color: SEMANTIC_COLORS.text.muted,
  },
  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  stat: {
    fontSize: 13,
    color: SEMANTIC_COLORS.text.secondary,
  },
});
